import { useCallback, useEffect, useRef, useState } from 'react';

import { api, ApiClientError } from '../../services/api';
import type { NvidiaTaskSummary } from '../../types';

type SlotStatus = {
  slots: NvidiaTaskSummary[];
  availableSlots: {
    slot: 'learning' | 'ads';
    label: string;
    defaultModel: string;
    models: string[];
  }[];
} | null;

type SlotKey = 'learning' | 'ads';

const SLOT_DESCRIPTIONS: Record<SlotKey, string> = {
  learning:
    'Bestemd voor automatische WhatsApp-replies (klantcontact). Kwaliteit gaat voor alles — gebruik het zwaarste beschikbare Nemotron 3 Ultra-model.',
  ads:
    'Bestemd voor ad-tekst-generatie in AdsManager. Lichter model (Nemotron 3 Super) is voldoende en houdt capaciteit vrij voor Learning.',
};

const NvidiaTaskCredentialsCard = () => {
  const [status, setStatus] = useState<SlotStatus>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState<SlotKey>('learning');
  const [selectedModel, setSelectedModel] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [action, setAction] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const data = await api.nvidiaTaskStatus();
      setStatus(data);
      const current = data.availableSlots.find((s) => s.slot === selectedSlot);
      const credential = data.slots.find((s) => s.slot === selectedSlot);
      if (current) {
        const preferred = credential?.model && current.models.includes(credential.model)
          ? credential.model
          : current.defaultModel;
        if (preferred) setSelectedModel(preferred);
      }
    } catch (reason) {
      setError(
        reason instanceof ApiClientError
          ? reason.message
          : 'Kon NVIDIA task-status niet ophalen.',
      );
    } finally {
      setLoading(false);
    }
  }, [selectedSlot]);

  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    void reload();
  }, [reload]);

  const handleSlotChange = (value: SlotKey) => {
    setSelectedSlot(value);
    if (status) {
      const current = status.availableSlots.find((s) => s.slot === value);
      const credential = status.slots.find((s) => s.slot === value);
      if (current) {
        const preferred = credential?.model && current.models.includes(credential.model)
          ? credential.model
          : current.defaultModel;
        if (preferred) setSelectedModel(preferred);
      }
    }
  };

  const handleSave = async () => {
    if (!apiKey.trim()) {
      setError('Voer een API-key in.');
      return;
    }
    setAction('save');
    setError(null);
    setNotice(null);
    try {
      await api.saveNvidiaTaskCredentials({
        slot: selectedSlot,
        apiKey: apiKey.trim(),
        model: selectedModel,
      });
      setApiKey('');
      setNotice('API-key veilig opgeslagen op de server.');
      void reload();
    } catch (reason) {
      setError(reason instanceof ApiClientError ? reason.message : 'Opslaan mislukt.');
    } finally {
      setAction(null);
    }
  };

  const handleTest = async () => {
    setAction('test');
    setError(null);
    setNotice(null);
    try {
      const result = await api.testNvidiaTask(selectedSlot);
      if (result.success) {
        setNotice(`Test geslaagd in ${result.latencyMs}ms.`);
      } else {
        const detail = result.errorMessage
          ? `${result.errorCode ?? 'onbekende fout'}: ${result.errorMessage}`
          : (result.errorCode ?? 'onbekende fout');
        setError(`Test mislukt (${result.latencyMs}ms): ${detail}`);
      }
      void reload();
    } catch (reason) {
      setError(reason instanceof ApiClientError ? reason.message : 'Testen mislukt.');
    } finally {
      setAction(null);
    }
  };

  const handleRemove = async () => {
    setAction('remove');
    setError(null);
    setNotice(null);
    try {
      await api.removeNvidiaTaskCredentials(selectedSlot);
      setNotice('Provider verwijderd.');
      void reload();
    } catch (reason) {
      setError(reason instanceof ApiClientError ? reason.message : 'Verwijderen mislukt.');
    } finally {
      setAction(null);
    }
  };

  if (loading) {
    return (
      <section className="settings-card">
        <h2>NVIDIA — Learning &amp; Ads-copy</h2>
        <p className="muted">Laden…</p>
      </section>
    );
  }

  if (!status) {
    return (
      <section className="settings-card">
        <h2>NVIDIA — Learning &amp; Ads-copy</h2>
        <p className="notice warning">Kon status niet ophalen.</p>
      </section>
    );
  }

  const currentModels =
    status.availableSlots.find((s) => s.slot === selectedSlot)?.models ?? [];
  const existingCredential = status.slots.find((s) => s.slot === selectedSlot);

  const statusText = !existingCredential
    ? 'Niet geconfigureerd'
    : existingCredential.testStatus === 'passed'
      ? 'Test geslaagd'
      : existingCredential.testStatus === 'failed'
        ? 'Test mislukt'
        : 'Geconfigureerd';

  return (
    <section className="settings-card">
      <div className="setting-heading">
        <div>
          <h2>NVIDIA — Learning &amp; Ads-copy</h2>
          <p className="muted">
            Twee onafhankelijke NVIDIA NIM API-keys: één voor automatische WhatsApp-replies
            (Learning) en één voor ad-tekst-generatie (Ads-copy). Een piek in de één blokkeert
            de ander nooit.
          </p>
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="nvidia-task-slot">Functie</label>
        <select
          id="nvidia-task-slot"
          value={selectedSlot}
          onChange={(event) => handleSlotChange(event.target.value as SlotKey)}
          disabled={action !== null}
        >
          {status.availableSlots.map((slot) => (
            <option key={slot.slot} value={slot.slot}>
              {slot.label}
            </option>
          ))}
        </select>
      </div>

      <p className="muted small">{SLOT_DESCRIPTIONS[selectedSlot]}</p>

      <div className="form-group">
        <label htmlFor="nvidia-task-model">Model</label>
        <select
          id="nvidia-task-model"
          value={selectedModel}
          onChange={(event) => setSelectedModel(event.target.value)}
          disabled={action !== null}
        >
          {currentModels.map((model) => (
            <option key={model} value={model}>{model}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="nvidia-task-api-key">API-key</label>
        <input
          id="nvidia-task-api-key"
          type="password"
          value={apiKey}
          onChange={(event) => setApiKey(event.target.value)}
          placeholder={existingCredential ? '•••••••• (opgeslagen)' : 'Plak hier je NVIDIA NIM API-key'}
          disabled={action !== null}
          autoComplete="off"
        />
      </div>

      {existingCredential && (
        <p className="muted small">
          Status: <strong>{statusText}</strong>
          {existingCredential.testLatencyMs !== null && ` — ${existingCredential.testLatencyMs}ms`}
        </p>
      )}

      {existingCredential?.lastError && (
        <p className="notice warning small" style={{ whiteSpace: 'pre-wrap' }}>
          Laatste fout: {existingCredential.lastError}
        </p>
      )}
      {error && <p className="notice warning" style={{ whiteSpace: 'pre-wrap' }}>{error}</p>}
      {notice && <p className="notice" role="status">{notice}</p>}

      <div className="button-row">
        <button
          type="button"
          className="button primary small"
          disabled={action !== null || !apiKey.trim()}
          onClick={handleSave}
        >
          {action === 'save' ? 'Bezig…' : 'API-key opslaan'}
        </button>
        <button
          type="button"
          className="button secondary small"
          disabled={action !== null || !existingCredential}
          onClick={handleTest}
        >
          {action === 'test' ? 'Bezig…' : 'Verbinding testen'}
        </button>
        <button
          type="button"
          className="button secondary small danger"
          disabled={action !== null || !existingCredential}
          onClick={handleRemove}
        >
          {action === 'remove' ? 'Bezig…' : 'Verwijderen'}
        </button>
      </div>
    </section>
  );
};

export default NvidiaTaskCredentialsCard;
